import {
    ConfigurationContext,
    ElectionDefinition,
    LoadedElectionDefinition,
    queries,
    useElectionToggle,
    useFeature,
} from '@news-mono/web-common'
import { UseQueryResult, useQuery } from '@tanstack/react-query'
import React, { useCallback, useContext } from 'react'
import { useLocation } from 'react-router'
import { ElectionDataFetchError, ElectionTitleButton } from '../components'
import { SeatData, getSeatsData } from '../data'
import { SeatCard } from '../SeatCard/SeatCard'
import { breakpoint } from '../../../__styling'
import { tokens } from '@news-mono/design-tokens'
import {
    TheSeatEmbedWidgetContainer,
    TheSeatEmbedWidgetLogo,
    TheSeatEmbedWidgetLogoLink,
    TheSeatEmbedWidgetTitleContainer,
} from './TheSeatEmbedWidget.styled'

export interface TheSeatEmbedWidgetProps {
    electionDefinition: ElectionDefinition
    mainRef?: React.RefObject<HTMLDivElement>
    fireClickEvent?: () => void
    electorateName?: string | null
    providedData?: SeatData
    imageBaseUrl?: string
}

export const TheSeatEmbedWidget = ({
    electionDefinition,
    mainRef,
    electorateName,
    providedData,
    fireClickEvent,
    imageBaseUrl,
}: TheSeatEmbedWidgetProps) => {
    const electionEmbedsEnabled = useFeature('custom-election-embeds')

    const waElectoratesPageEnabled = useFeature(
        'wa-election-electorate-results-route',
    )
    const federalElectoratesPageEnabled = useFeature(
        'federal-election-electorate-results-route',
    )

    const areElectorateRoutesEnabled =
        waElectoratesPageEnabled || federalElectoratesPageEnabled

    const { electionId, logo } = electionDefinition

    const isLandingPageEnabled = useElectionToggle(
        electionId,
        'election-landing-page-route',
    )

    const { search } = useLocation()
    const params = new URLSearchParams(search)

    // no name or data is provided, so assume it's not an embed, and we want to provide the actual
    // name from the provided url.
    if (!electorateName && !providedData) {
        electorateName = params.get('electorate')
    }

    // Resolves to the matching seat, or undefined if the electorate isn't in
    // the data. Missing election data throws, so it surfaces as result.isError.
    const selectSeat = useCallback(
        (definition: LoadedElectionDefinition) => {
            const electionData = definition.electionData.data

            if (!electionData) {
                throw new Error(`No election data for ${electionId}`)
            }

            return getSeatsData(electionData, imageBaseUrl, 64).find(
                (seat) =>
                    seat.seatName.toLowerCase() ===
                    electorateName?.toLowerCase(),
            )
        },
        [electionId, imageBaseUrl, electorateName],
    )

    const config = useContext(ConfigurationContext)
    const result = useQuery(
        queries['election-api'].definition({
            initialDefinition: electionDefinition,
            electionAPI: config.electionApi,
            caller: config.apiCallerHeader,
            select: selectSeat,
        }),
    )

    if (!electionEmbedsEnabled) {
        return null
    }

    const showElectoratesButton = areElectorateRoutesEnabled && result.isSuccess
    const seatName = result.data?.seatName

    const logoPicture = (
        <TheSeatEmbedWidgetLogo>
            {logo.desktop && (
                <source
                    media={breakpoint('sm').replace('@media ', '')}
                    srcSet={logo.desktop}
                />
            )}
            <img src={logo.mobile} alt={logo.alt} />
        </TheSeatEmbedWidgetLogo>
    )

    return (
        <TheSeatEmbedWidgetContainer ref={mainRef}>
            <TheSeatEmbedWidgetTitleContainer>
                {isLandingPageEnabled ? (
                    <TheSeatEmbedWidgetLogoLink
                        to={`/politics/${electionId}`}
                        aria-label={`Go to the ${logo.alt} page`}
                        onClick={fireClickEvent}
                    >
                        {logoPicture}
                    </TheSeatEmbedWidgetLogoLink>
                ) : (
                    logoPicture
                )}
                {showElectoratesButton && (
                    <ViewElectoratesButton
                        desktopButton
                        seatName={seatName}
                        electionId={electionId}
                        fireClickEvent={fireClickEvent}
                    />
                )}
            </TheSeatEmbedWidgetTitleContainer>
            <SeatEmbedContent result={result} electorateName={electorateName} />
            {showElectoratesButton && (
                <ViewElectoratesButton
                    desktopButton={false}
                    seatName={seatName}
                    electionId={electionId}
                    fireClickEvent={fireClickEvent}
                />
            )}
        </TheSeatEmbedWidgetContainer>
    )
}

// Sized to match the embed seat card so the widget doesn't jump in height.
const errorProps = {
    backgroundColor: tokens.thenightly.colors.palette.neutral[0],
    minHeight: 126,
}

const SeatEmbedContent = ({
    result,
    electorateName,
}: {
    result: UseQueryResult<SeatData | undefined>
    electorateName: string | null | undefined
}) => {
    if (result.isError) {
        return <ElectionDataFetchError {...errorProps} />
    }

    if (result.isLoading) {
        return <SeatCard isLoading isEmbed />
    }

    if (!result.data) {
        return (
            <ElectionDataFetchError
                {...errorProps}
                heading="Electorate not found"
                text={
                    electorateName
                        ? `We couldn't find an electorate called "${electorateName}".`
                        : 'No electorate was specified.'
                }
            />
        )
    }

    return <SeatCard isEmbed seat={result.data} />
}

const ViewElectoratesButton = ({
    desktopButton,
    seatName,
    electionId,
    fireClickEvent,
}: {
    desktopButton: boolean
    seatName: string | undefined
    electionId: string
    fireClickEvent?: () => void
}) => (
    <ElectionTitleButton
        to={
            seatName
                ? `/politics/${electionId}/electorates?search=${seatName.toLowerCase()}&display=expanded`
                : `/politics/${electionId}/electorates`
        }
        includeBorder={true}
        hideOnMobile={desktopButton}
        hideOnDesktop={!desktopButton}
        fireEvent={fireClickEvent}
        text={'View Electorates'}
    />
)
